import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, ShieldCheck, ShieldAlert, Trash2, ArrowLeft, Inbox, FileEdit } from "lucide-react";
import { Link } from "react-router-dom";
import ContentEditor from "../components/ContentEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "faith_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("inbox");

  const fetchContacts = async (tkn) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, {
        headers: { "X-Admin-Password": tkn },
      });
      setContacts(data.contacts || []);
      setEmailConfigured(!!data.email_configured);
      if (data.contacts?.length) setActive(data.contacts[0]);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not load contacts.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchContacts(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Signed in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setContacts([]);
    setActive(null);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this contact permanently?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { "X-Admin-Password": token },
      });
      setContacts((prev) => prev.filter((c) => c.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 grain" data-testid="admin-login-page">
        <form
          onSubmit={onLogin}
          className="w-full max-w-md border border-white/10 p-8 lg:p-10"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-[#E60000]" />
            <span className="font-display text-xl uppercase font-bold tracking-tight text-white">
              Faith Ahyi-Sena · Admin
            </span>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
            Restricted Access
          </div>
          <h1 className="font-display text-3xl uppercase font-bold tracking-tight text-white leading-none mb-6">
            Sign In
          </h1>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mb-2 font-bold">
              Password
            </span>
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm focus:border-[#E60000]"
              data-testid="admin-password-input"
            />
          </label>
          <button
            type="submit"
            disabled={loggingIn}
            className="mt-6 w-full bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-60 text-white px-6 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-colors inline-flex items-center justify-center gap-3"
            data-testid="admin-login-submit"
          >
            {loggingIn ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {loggingIn ? "Signing in..." : "Enter"}
          </button>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] grain" data-testid="admin-page">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-[#E60000]" />
            <span className="font-display font-black uppercase tracking-tight text-white text-lg">
              Faith <span className="text-[#8A8A8A]">· Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div
              className={`hidden sm:inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1.5 border ${
                emailConfigured
                  ? "border-[#22c55e]/40 text-[#22c55e]"
                  : "border-[#E60000]/40 text-[#E60000]"
              }`}
              data-testid="email-status"
            >
              {emailConfigured ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
              {emailConfigured ? "Email Live" : "Email Off"}
            </div>
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] hover:text-white"
              data-testid="admin-back-link"
            >
              <ArrowLeft size={12} /> Site
            </Link>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E60000] hover:text-[#E60000] text-white px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12 py-10 lg:py-12">
        {/* Tab nav */}
        <div className="flex items-center gap-2 border-b border-white/10 mb-10" data-testid="admin-tabs">
          <button
            onClick={() => setTab("inbox")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.25em] uppercase font-bold transition-colors -mb-px ${
              tab === "inbox"
                ? "text-white border-b-2 border-[#E60000]"
                : "text-[#8A8A8A] hover:text-white border-b-2 border-transparent"
            }`}
            data-testid="tab-inbox"
          >
            <Inbox size={14} /> Inbox
            {contacts.length > 0 && (
              <span className="ml-1 text-[#E60000]">{contacts.length}</span>
            )}
          </button>
          <button
            onClick={() => setTab("content")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.25em] uppercase font-bold transition-colors -mb-px ${
              tab === "content"
                ? "text-white border-b-2 border-[#E60000]"
                : "text-[#8A8A8A] hover:text-white border-b-2 border-transparent"
            }`}
            data-testid="tab-content"
          >
            <FileEdit size={14} /> Content Editor
          </button>
        </div>

        {tab === "content" ? (
          <ContentEditor token={token} />
        ) : (
          <>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-2">
              Inbox
            </div>
            <h1 className="font-display text-4xl lg:text-5xl uppercase font-bold tracking-tight text-white leading-none">
              Coach Inquiries
              <span className="text-[#8A8A8A] ml-4">{contacts.length}</span>
            </h1>
          </div>
          {!emailConfigured && (
            <p className="max-w-md text-xs tracking-[0.15em] uppercase text-[#E60000] leading-relaxed">
              Heads up: Gmail App Password not set. Submissions are saved here but no auto-email is sent.
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#8A8A8A]">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : contacts.length === 0 ? (
          <div className="border border-white/10 p-12 text-center text-[#8A8A8A]" data-testid="admin-empty">
            <Mail size={28} className="mx-auto mb-4 text-[#E60000]" />
            No coach inquiries yet. When a coach submits the form, it will land here.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <ul className="lg:col-span-5 border-t border-white/10 max-h-[70vh] overflow-y-auto" data-testid="admin-contacts-list">
              {contacts.map((c) => {
                const isActive = active?.id === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => setActive(c)}
                      className={`w-full text-left border-b border-white/10 px-4 py-5 hover:bg-[#111111] transition-colors ${
                        isActive ? "bg-[#111111] border-l-2 border-l-[#E60000]" : ""
                      }`}
                      data-testid={`admin-contact-item-${c.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-display text-lg uppercase font-bold tracking-tight text-white truncate">
                          {c.name}
                        </span>
                        <span className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A] shrink-0 ml-3">
                          {new Date(c.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-xs text-[#cfcfcf] truncate">{c.school}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A] mt-1">
                        {c.role} · {c.division}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="lg:col-span-7">
              {active ? (
                <article className="border border-white/10 p-8 lg:p-10" data-testid="admin-contact-detail">
                  <div className="flex items-start justify-between gap-6 mb-6">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-2">
                        {active.role} · {active.division}
                      </div>
                      <h2 className="font-display text-3xl lg:text-4xl uppercase font-bold tracking-tight text-white leading-tight">
                        {active.name}
                      </h2>
                      <div className="font-display text-lg uppercase text-[#8A8A8A] mt-1">
                        {active.school}
                      </div>
                    </div>
                    <button
                      onClick={() => onDelete(active.id)}
                      className="text-[#8A8A8A] hover:text-[#E60000] transition-colors"
                      title="Delete"
                      data-testid="admin-delete-active"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 border-t border-white/10 pt-6">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mb-1">Email</div>
                      <a
                        href={`mailto:${active.email}?subject=Re: Faith Ahyi-Sena Recruiting`}
                        className="text-sm text-white hover:text-[#E60000] break-all"
                      >
                        {active.email}
                      </a>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mb-1">Received</div>
                      <div className="text-sm text-white">
                        {new Date(active.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
                    Message
                  </div>
                  <p className="whitespace-pre-wrap text-[#cfcfcf] leading-relaxed">{active.message}</p>

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <a
                      href={`mailto:${active.email}?subject=Re: Faith Ahyi-Sena Recruiting&body=Hi ${active.name.split(" ")[0]},%0D%0A%0D%0AThanks so much for reaching out about Faith.`}
                      className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3333] text-white px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-colors"
                      data-testid="admin-reply-email"
                    >
                      <Mail size={14} /> Reply via Email
                    </a>
                  </div>
                </article>
              ) : (
                <div className="border border-white/10 p-12 text-center text-[#8A8A8A]">
                  Select a contact to view details.
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}
      </section>
    </main>
  );
}
